import axios from 'axios';

const MAX_RETRIES = 3;
const BASE_DELAY = 1000; // 1 second
const RETRYABLE_STATUS_CODES = [502, 503, 504];

class BaseService {
  constructor({ resource }) {
    this.resource = `${resource}`;
    this.http = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
    });

    // Request interceptor
    this.http.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.Application = import.meta.env.VITE_BIFROST;
      }
      // Initialize retry count
      config.__retryCount = config.__retryCount || 0;
      return config;
    });

    // Response interceptor with retry logic for 502/503/504 errors
    this.http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { config } = error;

        // Check if we should retry
        const shouldRetry = config
          && config.__retryCount < MAX_RETRIES
          && error.response
          && RETRYABLE_STATUS_CODES.includes(error.response.status);

        if (shouldRetry) {
          config.__retryCount += 1;

          // Algunos endpoints llevan headers de un solo uso (p. ej. el token de
          // reCAPTCHA de la consulta de cédula). Si la petición registró un
          // hook, se regeneran esos headers antes de reintentar.
          if (typeof config.__refreshHeaders === 'function') {
            try {
              const freshHeaders = await config.__refreshHeaders();
              config.headers = { ...config.headers, ...freshHeaders };
            } catch (refreshError) {
              return Promise.reject(refreshError);
            }
          }

          // Exponential backoff: 1s, 2s, 4s
          const delay = BASE_DELAY * 2 ** (config.__retryCount - 1);

          console.warn(
            `[BaseService] Retry ${config.__retryCount}/${MAX_RETRIES} for ${config.url} after ${delay}ms (status: ${error.response.status})`,
          );

          await new Promise((resolve) => setTimeout(resolve, delay));

          return this.http.request(config);
        }

        return Promise.reject(error);
      },
    );
  }

  getUrl(id) {
    return `${this.resource}${id}/`;
  }

  async create(data) {
    return this.http.post(this.resource, data);
  }

  async getList(params) {
    return this.http.get(this.resource, { params });
  }

  async get(id) {
    return this.http.get(this.getUrl(id));
  }

  async update(id, data) {
    return this.http.put(this.getUrl(id), data);
  }
}

export default BaseService;
